import { invoiceDownloadUrl } from "@/shared/utils/invoiceDownloadUrl";
import { overdueDays } from "@/shared/utils/overdueDays";

interface Money {
  value: number;
  currency: string;
}

export interface UnpaidInvoiceLike {
  id: string;
  expiredAt: string;
  expired: boolean;
  amount: Money;
  invoiceAmount?: Money | null;
  invoice?: { number: string } | null;
  invoiceUrl?: string | null;
}

export interface UnpaidInvoicesSummary<R extends UnpaidInvoiceLike> {
  /** Счёт с ближайшим сроком оплаты. */
  nearest: R;
  /** Всего неоплаченных по счётчику сервера. */
  count: number;
  /** Сколько ещё, кроме ближайшего. */
  others: number;
  /** К оплате по валютам: invoiceAmount, а без него amount, как на PDF. */
  totals: Money[];
  /** Дней просрочки ближайшего; ноль, если не просрочен. */
  overdueDays: number;
  /** Ссылка «Скачать PDF», только когда счёт ровно один. */
  downloadUrl: string | null;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Сводка для карточки неоплаченного счёта на дашборде партнёра. */
export function summarizeUnpaidInvoices<R extends UnpaidInvoiceLike>(
  rows: R[],
  count: number,
  now: Date = new Date()
): UnpaidInvoicesSummary<R> | null {
  if (rows.length === 0) return null;

  const sorted = [...rows].sort(
    (left, right) =>
      new Date(left.expiredAt).getTime() - new Date(right.expiredAt).getTime()
  );
  const nearest = sorted[0];

  const byCurrency = new Map<string, number>();
  for (const row of sorted) {
    const amount = row.invoiceAmount ?? row.amount;
    byCurrency.set(
      amount.currency,
      (byCurrency.get(amount.currency) ?? 0) + amount.value
    );
  }

  const total = Math.max(count, rows.length);

  return {
    nearest,
    count: total,
    others: total - 1,
    totals: [...byCurrency.entries()].map(([currency, value]) => ({
      value: round2(value),
      currency,
    })),
    overdueDays: nearest.expired ? overdueDays(nearest.expiredAt, now) : 0,
    downloadUrl:
      total === 1 && nearest.invoiceUrl
        ? invoiceDownloadUrl(nearest.invoiceUrl)
        : null,
  };
}
